import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { Card, StatusIconProtoProps, IconButton } from '@nike/eds';
import './guest-pass.css';
import { usePassPolicies } from '../../../context/PassPolicy';
import { GuestForm } from './guest-from/guest-form';
import dayjs from 'dayjs';
import { VisitorPass } from '../../../models/IVisitorPass';
import { GuestPassRequest } from '../../../models/IGuestPass';
import { useSponsorContext } from '../../../context/Sponsor';
import { setShortDateByCountry } from '../../../utils/date-helper';
import { translateWithPlaceholders } from 'utils/translation-helper';
import { useI18nContext } from 'context/language/use-i18n-context';
import { ts } from './i18n';
import { useUserContext } from 'context/user/context';

export interface Props {
  onDismiss: () => void;
  setSnackBar: Dispatch<
    SetStateAction<{
      showSnack: boolean;
      message: string;
      snackStatus: StatusIconProtoProps['status'];
    }>
  >;
  guestPassToEdit: VisitorPass | null;
  isModleOpen: boolean;
}
export const Guest: FC<Props> = ({ onDismiss, setSnackBar, guestPassToEdit, isModleOpen }) => {
  const passPoliciesContext = usePassPolicies();
  const { i18nString } = useI18nContext();
  const userInfo = useUserContext();
  const guestPassPolicies = passPoliciesContext.passPolicies
    .filter(x => x.passTypeId === 10)
    .sort((a, b) =>
      dayjs(a.availableEndDate || a.shoppingStartDate).diff(
        dayjs(b.availableEndDate || b.shoppingStartDate),
      ),
    );
  const { sponsor } = useSponsorContext();
  const [selectedCard, setSelectedCard] = useState<number>(0);
  const [guestPass, setGuestPass] = useState<GuestPassRequest>();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const cardsPerPage = 5;

  useEffect(() => {
    const index =
      guestPassPolicies.findIndex(x => x.passPolicyId === guestPassToEdit?.passPolicyId) > 0
        ? guestPassPolicies.findIndex(x => x.passPolicyId === guestPassToEdit?.passPolicyId)
        : 0;
    setSelectedCard(index);
    // Make sure the page containing the selected card is shown
    setCurrentPage(Math.floor(index / cardsPerPage));
  }, [guestPassToEdit]);

  const handleCardClick = (index: number) => {
    setSelectedCard(index);
    // Update current page to show the selected card
    setCurrentPage(Math.floor(index / cardsPerPage));
  };
  const dateFormat = (date: Date) => {
    return dayjs(date).format(setShortDateByCountry(userInfo?.passplayUser?.userCountry || ''));
  };
  useEffect(() => {
    if (guestPassPolicies.length > 0) {
      if (guestPassToEdit?.passId ?? 0 > 0) {
        const editPass: GuestPassRequest = {
          passId: guestPassToEdit?.passId ?? null,
          firstName: guestPassToEdit?.firstName ?? '',
          lastName: guestPassToEdit?.lastName ?? '',
          emailAddress: guestPassToEdit?.emailAddress ?? '',
          guestEmailLanguage: guestPassToEdit?.guestEmailLanguage ?? '',
          shoppingDateStart: dayjs(guestPassToEdit?.shoppingDateStart).format('YYYY-MM-DD') ?? '',
          shoppingDateEnd: dayjs(guestPassToEdit?.shoppingDateEnd).format('YYYY-MM-DD') ?? '',
          passLocationId: guestPassToEdit?.passLocationId ?? 0,
          timeSlotId: guestPassToEdit?.timeSlotId ?? 0,
          govtComplianceChecked: guestPassToEdit?.govtComplianceChecked ?? false,
        };
        setGuestPass(editPass);
      } else {
        const isSingleDay =
          !!guestPassPolicies &&
          dateFormat(guestPassPolicies[selectedCard].shoppingStartDate) ===
            dateFormat(guestPassPolicies[selectedCard].shoppingEndDate);
        const guestInitValues: GuestPassRequest = {
          passId: null,
          firstName:
            !!guestPassPolicies && guestPassPolicies[selectedCard].selfInviteOnly
              ? sponsor?.firstName
              : '',
          lastName:
            !!guestPassPolicies && guestPassPolicies[selectedCard].selfInviteOnly
              ? sponsor?.lastName
              : '',
          emailAddress: '',
          shoppingDateStart:
            !!guestPassPolicies && isSingleDay
              ? dateFormat(guestPassPolicies[selectedCard].shoppingStartDate)
              : '',
          shoppingDateEnd:
            !!guestPassPolicies && isSingleDay
              ? dateFormat(guestPassPolicies[selectedCard].shoppingStartDate)
              : '',
          guestEmailLanguage: '',
          passLocationId: 0,
          timeSlotId: 0,
          govtComplianceChecked: false,
        };
        setGuestPass(guestInitValues);
      }
    }
  }, [selectedCard]);
  useEffect(() => {
    if (!isModleOpen && selectedCard !== 0) {
      setSelectedCard(0);
    }
  }, [isModleOpen]);

  // Calculate total pages needed
  const totalPages = Math.ceil(guestPassPolicies.length / cardsPerPage);

  // Get current cards to display
  const startIndex = currentPage * cardsPerPage;
  const endIndex = Math.min(startIndex + cardsPerPage, guestPassPolicies.length);
  const currentCards = guestPassPolicies.slice(startIndex, endIndex);

  // Handle navigation
  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(0, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
  };

  return (
    <div className="pass_modal_width">
      <Card className="eds-spacing--p-24 section-border">
        {guestPassPolicies.length > cardsPerPage && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
            <IconButton
              label="Previous cards"
              aria-label="Previous cards"
              size="small"
              disabled={currentPage === 0}
              onClick={handlePrevPage}
              icon={'CaretCircleLeft'}
              variant="ghost"
            />
            <div style={{ textAlign: 'center' }}>
              Page {currentPage + 1} of {totalPages}
            </div>
            <IconButton
              label="Next cards"
              aria-label="Next cards"
              size="small"
              disabled={currentPage >= totalPages - 1}
              onClick={handleNextPage}
              icon={'CaretCircleRight'}
              variant="ghost"
            />
          </div>
        )}
        <div className="guest__card-container">
          {currentCards.map((pass, index) => {
            const actualIndex = startIndex + index;
            return (
              <Card
                className={`guest__card ${selectedCard === actualIndex ? 'selected' : `card_guest_color ${pass.passesAllowed !== 0 && (pass.passesUsed || 0) >= (pass.passesAllowed || 0) ? 'disabled' : ''}`}`}
                key={pass.passPolicyId}
                title={pass.displayName}
                role="button"
                onClick={() => handleCardClick(actualIndex)}
              >
                <h3 className="txt_font">{pass.displayName.toUpperCase()}</h3>
                <p className="span_font">
                  ({dateFormat(pass.shoppingStartDate)} - {dateFormat(pass.availableEndDate)})
                </p>
                {pass.passesAllowed > 0 ? (
                  <p className="span_font">
                    {translateWithPlaceholders(
                      i18nString(ts.PASS_MODAL_AVAILABLE),
                      pass.passesAllowed - (pass.passesUsed || 0),
                      pass.passesAllowed,
                    )}
                  </p>
                ) : (
                  <p className="span_font">Unlimited passes</p>
                )}
              </Card>
            );
          })}
        </div>
      </Card>
      {!!guestPassPolicies && !!guestPassPolicies[selectedCard] && guestPass && (
        <GuestForm
          passPolicy={
            guestPassPolicies.find(
              x => x.passPolicyId === guestPassPolicies[selectedCard].passPolicyId,
            ) ?? guestPassPolicies[selectedCard]
          }
          guestPassToEdit={guestPass}
          onDismiss={onDismiss}
          setSnackbar={setSnackBar}
        />
      )}
    </div>
  );
};
